// Indian-aware formatting utilities.
// Per BRAND_GUIDE.md §4 — Indian comma grouping (1,23,456.78) and tabular figures are non-negotiable.

const NBSP = ' '; // non-breaking space between ₹ and amount

/**
 * Format an INR amount with Indian comma grouping.
 *   1234567.89  → "₹ 12,34,567.89"
 *   -3400.5     → "(₹ 3,400.50)"   parenthesised, never minus-prefixed
 *
 * Default: 2 decimal places. Override with `decimals`.
 */
export function formatINR(amount: number | string | null | undefined, opts?: { decimals?: number; signed?: boolean }): string {
  if (amount === null || amount === undefined || amount === '') return '—';
  const num = typeof amount === 'string' ? Number(amount) : amount;
  if (!Number.isFinite(num)) return '—';
  const decimals = opts?.decimals ?? 2;
  const abs = Math.abs(num);
  const grouped = abs.toLocaleString('en-IN', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  if (num < 0) return `(₹${NBSP}${grouped})`;
  const sign = opts?.signed && num > 0 ? '+' : '';
  return `${sign}₹${NBSP}${grouped}`;
}

/**
 * Format a delta with arrow + colour tone hint.
 * Returns the formatted text + a tone string ('positive'/'negative'/'neutral') so callers can class accordingly.
 */
export function formatINRDelta(amount: number | null | undefined): { text: string; tone: 'positive' | 'negative' | 'neutral' } {
  if (amount === null || amount === undefined || !Number.isFinite(amount)) {
    return { text: '—', tone: 'neutral' };
  }
  const tone: 'positive' | 'negative' | 'neutral' =
    amount > 0 ? 'positive' : amount < 0 ? 'negative' : 'neutral';
  const arrow = amount > 0 ? '↑' : amount < 0 ? '↓' : '·';
  const formatted = formatINR(Math.abs(amount));
  return { text: `${arrow} ${formatted}`, tone };
}

/**
 * Format a number as a percentage with one decimal.
 *   0.047 → "+4.7%"
 *   -0.018 → "-1.8%"
 */
export function formatPercent(ratio: number | null | undefined, opts?: { signed?: boolean; decimals?: number }): string {
  if (ratio === null || ratio === undefined || !Number.isFinite(ratio)) return '—';
  const decimals = opts?.decimals ?? 1;
  const pct = ratio * 100;
  const sign = opts?.signed && pct > 0 ? '+' : '';
  return `${sign}${pct.toFixed(decimals)}%`;
}

/**
 * Local-timezone yyyy-mm-dd for <input type="date"> values and API date params.
 * NEVER use `date.toISOString().slice(0, 10)` for a locally-constructed date:
 * IST midnight converts to the PREVIOUS day in UTC, which silently shifted
 * every month/FY preset back one day (GSTR windows dropped the last day of
 * the month and included the previous month's last day).
 */
export function toLocalIsoDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Shared date presets for report/voucher filters. All built on toLocalIsoDate
 * so the IST off-by-one class of bug (see above) can't recur in per-page copies.
 */
export function todayIso(): string {
  return toLocalIsoDate(new Date());
}

/** Local date n days back, as yyyy-mm-dd. */
export function daysAgoIso(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return toLocalIsoDate(d);
}

/** First day of the current month, as yyyy-mm-dd. */
export function monthStartIso(): string {
  const d = new Date();
  return toLocalIsoDate(new Date(d.getFullYear(), d.getMonth(), 1));
}

/** 1 April of the current Indian fiscal year (Apr–Mar), as yyyy-mm-dd. */
export function fyStartIso(): string {
  const d = new Date();
  const y = d.getMonth() < 3 ? d.getFullYear() - 1 : d.getFullYear();
  return toLocalIsoDate(new Date(y, 3, 1));
}

/**
 * Human label for an UPPER_SNAKE enum value: SUNDRY_CREDITORS → "Sundry Creditors",
 * NOT_MSME → "Not MSME". Raw enum text must never reach the user's eyes.
 */
export function humaniseEnum(value: string | null | undefined): string {
  if (!value) return '—';
  const KEEP_UPPER = new Set(['MSME', 'GST', 'GSTIN', 'TDS', 'TCS', 'HSN', 'SAC', 'PAN', 'OD', 'HQ']);
  return String(value)
    .split('_')
    .map((w) => (KEEP_UPPER.has(w) ? w : w.charAt(0) + w.slice(1).toLowerCase()))
    .join(' ');
}

/**
 * Format date per Tally convention: DD-MMM-YYYY (e.g. "29-May-2026").
 */
export function formatDateDDMMMYYYY(input: Date | string | number | null | undefined): string {
  if (input === null || input === undefined) return '—';
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return '—';
  const day = String(d.getDate()).padStart(2, '0');
  const month = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][d.getMonth()];
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
}

/**
 * Format date + time per Tally convention: "29-May-2026 14:22".
 */
export function formatDateTime(input: Date | string | number | null | undefined): string {
  if (input === null || input === undefined) return '—';
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return '—';
  const base = formatDateDDMMMYYYY(d);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${base} ${hh}:${mm}`;
}

/**
 * Voucher number — preserved as-is (validators handle the format like INV/FY26-27/MUM/0143).
 */
export function formatVoucherNo(no: string | null | undefined): string {
  return no ?? '—';
}

/**
 * Indian fiscal year for a given date. April–March.
 * 2026-05-29 → "FY26-27"
 * 2026-02-15 → "FY25-26"
 */
export function fiscalYearForDate(input: Date | string | number = new Date()): string {
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return '—';
  const month = d.getMonth(); // 0 = Jan
  const year = d.getFullYear();
  const startYear = month >= 3 ? year : year - 1; // month >=3 means Apr or later
  const endYear = startYear + 1;
  return `FY${String(startYear).slice(-2)}-${String(endYear).slice(-2)}`;
}

/**
 * Truncate text with ellipsis at the character boundary.
 */
export function truncate(text: string, max: number): string {
  if (!text) return '';
  return text.length <= max ? text : text.slice(0, max - 1) + '…';
}

/**
 * Strip machine tags from a narration before display. Bank-import vouchers
 * store "[BANK:<dedupe-key>] <bank narration>" — the key must stay in the DB
 * (it is the import idempotency check) but means nothing to a reader.
 */
export function displayNarration(narration: string | null | undefined): string {
  if (!narration) return '';
  return narration.replace(/^\[BANK:[^\]]+\]\s*/, '');
}

/**
 * "1 row" / "3 rows" — count + correctly-pluralised noun.
 */
export function pluralize(count: number, singular: string, plural?: string): string {
  return `${count} ${count === 1 ? singular : (plural ?? singular + 's')}`;
}
